// Page « Qualité des contrôles » (F-037) : banc de mesure sur un corpus synthétique, historique des exécutions.
import { useState } from "react";
import { post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Loading, Notice, PageHeader, Table, useToast } from "@core/ui";

type RuleResult = {
  rule: string;
  label: string;
  tp: number;
  fp: number;
  fn: number;
  tn: number;
  precision_permille: number | null;
  recall_permille: number | null;
  cases: { id: string; description: string; expected: boolean; detected: boolean; outcome: string; difficulty: string }[];
};

type Run = { id: string; rule_version: string; corpus_version: string; corpus_size: number; created_at: string; author: string | null; results: { rules: RuleResult[]; avertissement: string; duree_ms: number } };

function rate(permille: number | null, num: number, den: number): string {
  if (permille === null) return "—";
  return `${(permille / 10).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} % (${num} sur ${den})`;
}

const OUTCOMES: Record<string, [string, "succes" | "attention" | "erreur" | "neutre"]> = {
  vrai_positif: ["Écart détecté", "succes"],
  vrai_negatif: ["Aucun signalement, à raison", "succes"],
  faux_positif: ["Signalement sans écart injecté", "attention"],
  faux_negatif: ["Écart non détecté", "erreur"],
};

const DIFFICULTY: Record<string, string> = { simple: "simple", moyen: "intermédiaire", difficile: "difficile" };

export function QualitePage() {
  const org = useOrg();
  const path = org.api("/controles/qualite");
  const q = useGet<{ items: Run[] }>(path);
  const invalidate = useInvalidate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const latest = q.data?.items[0];
  return (
    <div className="page">
      <PageHeader
        eyebrow="Location"
        title="Qualité des contrôles"
        intro="Mesure des règles de contrôle sur un corpus synthétique généré par Bailivo : faux positifs, faux négatifs, précision et rappel, règle par règle."
        actions={
          <Button
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await post(path);
                toast("Mesure terminée.");
                invalidate(path);
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            Lancer une mesure
          </Button>
        }
      />
      <Notice tone="attention" title="Une mesure synthétique n’est pas un taux réel">
        Le corpus est composé de documents fictifs, avec des cas simples et difficiles. Il vérifie un comportement attendu ; il ne mesure pas la détection en situation réelle. Aucun chiffre de cette page ne peut servir d’argument commercial : un corpus licite de documents authentiques et un pilote supervisé restent nécessaires.
      </Notice>
      <ErrorBox error={error || q.error} />
      {q.isLoading ? <Loading /> : null}
      {q.data && !latest ? <Empty title="Aucune mesure">Lancez une première mesure pour obtenir les résultats par règle.</Empty> : null}
      {latest ? (
        <Card title={`Mesure du ${dateTime(latest.created_at)}`}>
          <p className="small muted">
            Corpus <span className="mono">{latest.corpus_version}</span> · {latest.corpus_size} cas · règles <span className="mono">{latest.rule_version}</span> · {latest.results.duree_ms} ms
            {latest.author ? ` · lancée par ${latest.author}` : ""}
          </p>
          <Table caption="Résultats par règle">
            <thead>
              <tr>
                <th>Règle</th>
                <th className="right">Écarts détectés</th>
                <th className="right">Signalements à tort</th>
                <th className="right">Écarts manqués</th>
                <th className="right">Cas sans écart</th>
                <th>Précision</th>
                <th>Rappel</th>
              </tr>
            </thead>
            <tbody>
              {latest.results.rules.map((r) => (
                <tr key={r.rule}>
                  <td>{r.label}</td>
                  <td className="right num">{r.tp}</td>
                  <td className="right num">{r.fp}</td>
                  <td className="right num">{r.fn}</td>
                  <td className="right num">{r.tn}</td>
                  <td className="num">{rate(r.precision_permille, r.tp, r.tp + r.fp)}</td>
                  <td className="num">{rate(r.recall_permille, r.tp, r.tp + r.fn)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {latest.results.rules.map((r) => (
            <details key={r.rule} className="controle-details">
              <summary>
                Cas de la règle « {r.label} » ({r.cases.length})
              </summary>
              <ul className="list-plain controle-cases">
                {r.cases.map((c) => {
                  const [label, tone] = OUTCOMES[c.outcome] ?? [c.outcome, "neutre"];
                  return (
                    <li key={c.id} className="row" style={{ flexWrap: "wrap" }}>
                      <span className="mono">{c.id}</span> {c.description} <span className="muted">· {DIFFICULTY[c.difficulty] ?? c.difficulty}</span> <Badge tone={tone}>{label}</Badge>
                    </li>
                  );
                })}
              </ul>
            </details>
          ))}
          <p className="small muted">{latest.results.avertissement}</p>
        </Card>
      ) : null}
      {q.data && q.data.items.length > 1 ? (
        <Card title="Mesures précédentes">
          <Table caption="Historique des mesures">
            <thead>
              <tr>
                <th>Date</th>
                <th>Corpus</th>
                <th>Règles</th>
                <th className="right">Cas</th>
                <th className="right">Écarts manqués</th>
                <th className="right">Signalements à tort</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((r) => (
                <tr key={r.id}>
                  <td>{dateTime(r.created_at)}</td>
                  <td className="mono">{r.corpus_version}</td>
                  <td className="mono">{r.rule_version}</td>
                  <td className="right num">{r.corpus_size}</td>
                  <td className="right num">{r.results.rules.reduce((s, x) => s + x.fn, 0)}</td>
                  <td className="right num">{r.results.rules.reduce((s, x) => s + x.fp, 0)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}
